import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ExternalLink, PenLine } from "lucide-react";
import { formatDateTime } from "../api";
import { useAuth } from "../auth";
import { useClientSelection } from "../client-context";
import { useApi } from "../hooks";
import { fmtInt } from "../ads";
import { reportRangeQuery } from "../date-range";
import { firstLine, facebookPostFormat, pctChange, tone, type FbPageReport, type FbPost } from "../facebook";
import { AdsRange } from "../components/AdsRange";
import { AreaChart, ChartCard, ChartEmpty } from "../components/Charts";
import { EmptyState, ErrorState, MetricCard, PageHeader, PageSkeleton } from "../components/Ui";
import { NoClient } from "../components/Workflow";
import { AccountPicker, ChangesList, Freshness, SyncButton, WritesNotice } from "../components/facebook/FbShared";

const shortDate = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });
type Metric = "viewers" | "engagements" | "new_follows";
const METRIC_WORDS: Record<Metric, string> = { viewers: "Viewers", engagements: "Engagements", new_follows: "New follows" };

/** A client's Facebook page: followers, reach, engagement, its posts, and publishing a new post. */
export default function FacebookPage() {
  const { clientId } = useClientSelection();
  if (!clientId) return <><PageHeader title="Facebook page" description="Pick a client in the sidebar." /><NoClient /></>;
  return <PageView key={clientId} clientId={clientId} />;
}

function PageView({ clientId }: { clientId: number }) {
  const { user } = useAuth();
  const admin = user?.role === "superadmin";
  const canAsk = user?.role === "superadmin" || user?.role === "exec";
  const [params] = useSearchParams();
  const link = params.get("link") || "";
  const { data, loading, error, reload } = useApi<FbPageReport>(`/api/facebook/${clientId}/page?${reportRangeQuery(params)}${link ? `&link=${link}` : ""}`);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [metric, setMetric] = useState<Metric>("viewers");
  const [sort, setSort] = useState<"newest" | "engaged">("newest");
  const [content, setContent] = useState<"all" | "reel" | "post">("all");
  const [runBusy, setRunBusy] = useState(false);
  const say = (text: string, ok: boolean) => { setNotice({ ok, text }); reload(); };
  const posts = useMemo(() => {
    const list = (data?.posts || []).filter(p => content === "all" || facebookPostFormat(p) === content);
    if (sort === "engaged") list.sort((a, b) => (b.engagements ?? -1) - (a.engagements ?? -1));
    return list;
  }, [data?.posts, sort, content]);

  if (loading && !data) return <><PageHeader title="Facebook page" description="Loading…" /><PageSkeleton cards={3} /></>;
  if (error || !data) return <><PageHeader title="Facebook page" /><ErrorState message={error || "The Facebook page could not load."} onRetry={reload} /></>;
  if (!data.link || !data.period || !data.totals) {
    return (
      <>
        <PageHeader title="Facebook page" />
        <EmptyState title="No Facebook page linked"
          description={admin ? "Link this client's Facebook page through Windsor in Setup, Connections. Followers, reach and posts show here after the first read." : "A superadmin links the page in Setup, Connections."}
          action={admin ? <Link className="button secondary" to={`/client-settings?client_id=${clientId}`}>Open Connections</Link> : undefined} />
      </>
    );
  }
  const { totals, previous, period, changes = [] } = data;
  const acc = data.link;
  const days = data.daily || [];
  const series = days.map((d) => ({ label: shortDate(d.date), value: d[metric] ?? 0 }));
  const hasDays = days.some((d) => d[metric]);
  // When Meta shares no page figures at all (the Freshness line says why), the empty chart and the
  // "no comparison" line only repeat that: the posts table is what is left to show.
  const anyFigures = days.some((d) => (Object.keys(METRIC_WORDS) as Metric[]).some((k) => d[k]));
  const ch = (k: "new_follows" | "viewers_avg" | "engagements" | "page_views" | "posts") => (previous ? pctChange(totals[k], previous[k]) : null);

  return (
    <div className="fb-page">
      <PageHeader
        title="Facebook page"
        description={`${acc.account_name}. ${shortDate(period.from)} to ${shortDate(period.to)}.`}
        actions={<>
          <AccountPicker links={data.links} current={acc} label="Facebook page" />
          <AdsRange />
          <SyncButton clientId={clientId} kind="page" onDone={say} />
          {canAsk && <Link className="button" to={`/publishing?client_id=${clientId}&channel=facebook`}><PenLine aria-hidden="true" />Write or schedule a post</Link>}
        </>}
      />
      <Freshness link={acc} />
      {notice && <p className={`inline-notice ${notice.ok ? "success" : "danger"}`} role={notice.ok ? "status" : "alert"}><span>{notice.text}</span><button type="button" aria-label="Dismiss" onClick={() => setNotice(null)}>×</button></p>}
      {canAsk && <WritesNotice link={acc} admin={admin} clientId={clientId} onChanged={reload} what="publish posts" />}

      <section className="metric-grid ads-metrics fb-metrics" aria-label="Totals">
        <MetricCard label="Followers" value={fmtInt(totals.followers)} detail={totals.followers_change !== null ? `${totals.followers_change >= 0 ? "+" : ""}${fmtInt(totals.followers_change)} in this range` : undefined} />
        <MetricCard label="New follows" value={fmtInt(totals.new_follows)} change={ch("new_follows")} tone={tone("new_follows", ch("new_follows"))} detail={totals.unfollows ? `${fmtInt(totals.unfollows)} unfollowed` : undefined} />
        <MetricCard label="Viewers a day" value={fmtInt(totals.viewers_avg)} change={ch("viewers_avg")} tone={tone("viewers_avg", ch("viewers_avg"))} />
        <MetricCard label="Engagements" value={fmtInt(totals.engagements)} change={ch("engagements")} tone={tone("engagements", ch("engagements"))} />
        <MetricCard label="Page views" value={fmtInt(totals.page_views)} change={ch("page_views")} tone={tone("page_views", ch("page_views"))} />
        <MetricCard label="Posts and reels" value={fmtInt(totals.posts)} change={ch("posts")} tone={tone("posts", ch("posts"))} />
      </section>
      {anyFigures && <p className="ads-note">{previous ? `Compared with ${shortDate(period.previous.from)} to ${shortDate(period.previous.to)}.` : "No page figures for the period before, so there is no comparison."}</p>}

      {anyFigures && <div className="dashboard-charts">
        <ChartCard className="chart-wide" eyebrow="Facebook" title={`${METRIC_WORDS[metric]} per day`}
          meta={<div className="segmented-control compact" aria-label="Chart metric">
            {(Object.keys(METRIC_WORDS) as Metric[]).map((k) => <button key={k} type="button" className={metric === k ? "active" : ""} aria-pressed={metric === k} onClick={() => setMetric(k)}>{METRIC_WORDS[k]}</button>)}
          </div>}>
          {hasDays ? <AreaChart integer data={series} format={fmtInt} caption={`${METRIC_WORDS[metric]} per day`} /> : <ChartEmpty>No page figures in this range yet.</ChartEmpty>}
        </ChartCard>
      </div>}

      <section className="panel fb-posts">
        <div className="panel-heading">
          <div><h2>Posts and reels</h2><p className="panel-note">Posted in this range. Figures are for the post's whole life.</p></div>
          <select className="campaigns-account" aria-label="Facebook content" value={content} onChange={e => setContent(e.target.value as typeof content)}>
            <option value="all">Posts and reels</option><option value="reel">Reels</option><option value="post">Posts</option>
          </select>
          <div className="segmented-control compact" aria-label="Sort posts">
            <button type="button" className={sort === "newest" ? "active" : ""} aria-pressed={sort === "newest"} onClick={() => setSort("newest")}>Newest</button>
            <button type="button" className={sort === "engaged" ? "active" : ""} aria-pressed={sort === "engaged"} onClick={() => setSort("engaged")}>Most engaged</button>
          </div>
        </div>
        {posts.length ? (
          <div className="table-wrap">
            <table className="ads-table fb-post-table">
              <thead><tr><th>Post</th><th className="num">Viewers</th><th className="num">Engagements</th><th className="num fb-col-extra">Reactions</th><th className="num fb-col-extra">Comments</th><th className="num fb-col-extra">Shares</th><th className="num fb-col-extra">Clicks</th><th><span className="sr-only">Open</span></th></tr></thead>
              <tbody>{posts.map((p) => <PostRow key={p.post_id} post={p} />)}</tbody>
            </table>
          </div>
        ) : <EmptyState title={content === "reel" ? "No reels in this range" : "No posts in this range"} description={canAsk ? "Nothing was posted in these dates. Write one with New post." : "Nothing was posted in these dates."} />}
      </section>

      <ChangesList changes={changes} admin={admin} clientId={clientId} onRan={say} busy={runBusy} setBusy={setRunBusy} />
    </div>
  );
}

function PostRow({ post: p }: { post: FbPost }) {
  return (
    <tr>
      <td className="fb-post-cell">
        <div className="fb-post">
          {p.picture ? <img src={p.picture} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} /> : <span className="fb-ad-blank" aria-hidden="true" />}
          <div>
            <span className="fb-post-text">{firstLine(p.message) || <span className="muted">Photo or video, no text</span>}</span>
            <small className="muted">{facebookPostFormat(p) === "reel" ? "Reel" : "Post"}, {p.created_at ? formatDateTime(p.created_at) : "Date unknown"}</small>
          </div>
        </div>
      </td>
      <td className="num" data-label="Viewers">{fmtInt(p.viewers)}</td>
      <td className="num" data-label="Engagements">{fmtInt(p.engagements)}</td>
      <td className="num fb-col-extra" data-label="Reactions">{fmtInt(p.reactions)}</td>
      <td className="num fb-col-extra" data-label="Comments">{fmtInt(p.comments)}</td>
      <td className="num fb-col-extra" data-label="Shares">{fmtInt(p.shares)}</td>
      <td className="num fb-col-extra" data-label="Clicks">{fmtInt(p.clicks)}</td>
      <td className="campaigns-actions">{p.permalink && <a className="icon-button" href={p.permalink} target="_blank" rel="noreferrer noopener" aria-label="Open the post on Facebook"><ExternalLink aria-hidden="true" /></a>}</td>
    </tr>
  );
}
